import React, { useState, useEffect, useRef } from 'react';
import { Card, List, Avatar, Badge, message, Spin, Empty } from 'antd';
import { UserOutlined, MessageOutlined } from '@ant-design/icons';
import { useOutletContext } from 'react-router-dom';
import { getChatSessions } from '../../api/chat';
import ChatModal from './components/ChatModal';
import './index.css';

const UserMessages = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  
  // 从MainLayout获取WebSocket引用和刷新函数
  const { wsRef, refreshUnreadCount } = useOutletContext();

  useEffect(() => {
    // 进入页面时获取会话列表
    fetchSessions();
  }, []);

  // 获取聊天会话列表
  const fetchSessions = async () => {
    try {
      setLoading(true);
      const result = await getChatSessions();
      if (result.code === 200 && result.data) {
        setSessions(result.data);
      } else {
        message.error(result.msg || '获取消息列表失败');
      }
    } catch (error) {
      console.error('获取消息列表失败:', error);
      message.error('获取消息列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 格式化时间
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      const date = new Date(timeStr);
      const now = new Date();
      const diff = now - date;
      
      // 小于1分钟
      if (diff < 60000) {
        return '刚刚';
      }
      // 小于1小时
      if (diff < 3600000) {
        return `${Math.floor(diff / 60000)}分钟前`;
      }
      // 小于24小时
      if (diff < 86400000) {
        return `${Math.floor(diff / 3600000)}小时前`;
      }
      // 超过24小时，显示日期
      return date.toLocaleDateString('zh-CN', { 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return timeStr;
    }
  };

  // 打开聊天窗口
  const handleOpenChat = (session) => {
    setCurrentSession(session);
    setChatVisible(true);
    // 打开对话窗口后延迟刷新会话列表和未读数
    setTimeout(() => {
      fetchSessions();
      if (refreshUnreadCount) {
        refreshUnreadCount();
      }
    }, 500);
  };

  // 关闭聊天窗口
  const handleCloseChat = () => {
    setChatVisible(false);
    setCurrentSession(null);
    // 关闭后刷新会话列表和未读数
    fetchSessions();
    if (refreshUnreadCount) {
      refreshUnreadCount();
    }
  };

  return (
    <div className="user-messages">
      <Card 
        title="用户消息" 
        bordered={false}
        extra={
          <span style={{ fontSize: '14px', color: '#999', fontWeight: 'normal' }}>
            共 {sessions.length} 条会话
          </span>
        }
      >
        <Spin spinning={loading}>
          {sessions.length === 0 && !loading ? (
            <Empty description="暂无消息" />
          ) : (
            <List
              className="message-list"
              itemLayout="horizontal"
              dataSource={sessions}
              renderItem={(item) => (
                <List.Item
                  className="message-item"
                  onClick={() => handleOpenChat(item)}
                  extra={
                    item.unreadCount > 0 && (
                      <Badge 
                        count={item.unreadCount} 
                        style={{ backgroundColor: '#ff4d4f' }}
                      />
                    )
                  }
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        size={48}
                        src={item.userAvatar} 
                        icon={<UserOutlined />}
                      />
                    }
                    title={
                      <div className="message-title">
                        <span className="user-name">{item.userName}</span>
                        <span className="user-id">ID: {item.userId}</span>
                      </div>
                    }
                    description={
                      <div className="message-content">
                        <div className="last-message">
                          <MessageOutlined style={{ marginRight: 4 }} />
                          {item.lastMessage || '暂无消息'}
                        </div>
                        <div className="message-time">
                          {formatTime(item.lastMessageTime)}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Card>

      {/* 聊天Modal，传递wsRef */}
      <ChatModal
        visible={chatVisible}
        session={currentSession}
        onClose={handleCloseChat}
        wsRef={wsRef}
      />
    </div>
  );
};

export default UserMessages;
